/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Buildings,
  CheckCircle,
  Coins,
  CurrencyCircleDollar,
  Eye,
  FileDotted,
  FileText,
  FileX,
  FunnelSimple,
  Hourglass,
  Info,
  Receipt,
  Scales,
  Warning,
  XCircle,
} from 'phosphor-react';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { IData, ISelect } from '../../../../types/shared';
import { ICompany, useCompanies } from '../../../../hooks/useCompanies';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import MultiSelectInputForm from '../../../../components/inputs/forms/MultiSelectInputForm';
import { getFormattedLabelString } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { useEmpresaQuota } from '../../../../hooks/useEmpresaQuota';
import { api } from '../../../../services/api';
import { EmpresaCard } from './EmpresaCard';
import { CONFIG_REGIME, Regime } from './regras';

moment.locale('pt-br');

const ITEMS_PER_PAGE = 50;

// Só estas duas telas descontam do faturamento a receita financeira que o
// Domínio marca como faturamento. Vai fixo na query (não na URL): link antigo
// salvo sem o parâmetro também desconta.
const PARAMS_TELA = { sem_receitas_financeiras: 'true' };

/** Filtros avançados do drawer: nome no form = nome do parâmetro da API. */
const FILTROS_AVANCADOS: { name: string; placeholder: string }[] = [
  { name: 'cidade', placeholder: 'Filtrar por cidade' },
  { name: 'uf', placeholder: 'Filtrar por Estado' },
  { name: 'tags', placeholder: 'Filtrar por tags' },
  { name: 'comercial', placeholder: 'Filtrar por comercial' },
  { name: 'contabil', placeholder: 'Filtrar pelo contábil' },
  { name: 'financeiro', placeholder: 'Filtrar pelo financeiro' },
  { name: 'contas_a_pagar', placeholder: 'Filtrar por contas à pagar' },
  { name: 'encerramento_anual', placeholder: 'Filtrar por encerramento anual' },
  { name: 'fiscal', placeholder: 'Filtrar por fiscal' },
  { name: 'imposto_de_renda', placeholder: 'Filtrar por imposto de renda' },
  { name: 'integracao_contabil', placeholder: 'Filtrar por integração contábil' },
  { name: 'integracao_fiscal', placeholder: 'Filtrar por integração fiscal' },
  { name: 'pessoal', placeholder: 'Filtrar por pessoal' },
  { name: 'relacionamento_com_cliente', placeholder: 'Filtrar por relacionamento com cliente' },
  { name: 'societario', placeholder: 'Filtrar por societário' },
];

const OCULTAR_OPTIONS = [
  { label: 'Ocultar zerados', value: 'zerados' },
  { label: 'Ocultar valores', value: 'valores' },
];

interface ITotais {
  [k: string]: any;
  fatura_periodo_inicio?: string;
  fatura_periodo_fim?: string;
  /** De quando é a conferência dos XML do mês (null = ainda calculando). */
  xml_calculado_em?: string | null;
}

interface IKpi {
  situacao: string;
  campo: string;
  rotulo: string;
  cor: string;
  icone: React.ReactNode;
  dica?: string;
  semPercentual?: boolean;
}

const KPIS_APURACAO: IKpi[] = [
  {
    situacao: 'total_apuracao',
    campo: 'total_empresas',
    rotulo: 'Total empresas',
    cor: '#0F172A',
    icone: <Buildings size={18} weight="duotone" />,
    semPercentual: true,
  },
  {
    situacao: 'apuradas_com_faturamento',
    campo: 'apuradas_com_faturamento',
    rotulo: 'Apuradas c/ faturamento',
    cor: '#6366F1',
    icone: <CheckCircle size={18} weight="duotone" />,
  },
  {
    situacao: 'apuradas_sem_faturamento',
    campo: 'apuradas_sem_faturamento',
    rotulo: 'Apuradas s/ faturamento',
    cor: '#F97316',
    icone: <CurrencyCircleDollar size={18} weight="duotone" />,
  },
  {
    situacao: 'nao_apuradas',
    campo: 'nao_apuradas',
    rotulo: 'Não apuradas c/ fat.',
    cor: '#D97706',
    icone: <Hourglass size={18} weight="duotone" />,
  },
  {
    situacao: 'faturamento_zerado',
    campo: 'faturamento_zerado',
    rotulo: 'Não apuradas s/ fat.',
    cor: '#2563EB',
    icone: <CurrencyCircleDollar size={18} weight="duotone" />,
  },
];

const KPIS_CONFERENCIA: IKpi[] = [
  {
    situacao: 'ok',
    campo: 'ok',
    rotulo: 'Sem erro',
    cor: '#10B981',
    icone: <CheckCircle size={18} weight="duotone" />,
  },
  {
    situacao: 'erro',
    campo: 'erro',
    rotulo: 'Com erro',
    cor: '#F43F5E',
    icone: <XCircle size={18} weight="duotone" />,
  },
  {
    situacao: 'sem_guia',
    campo: 'sem_guia',
    rotulo: 'Sem guias PIS/COFINS',
    cor: '#64748B',
    icone: <FileText size={18} weight="duotone" />,
    dica: 'Em processo de auditoria automático',
  },
  {
    situacao: 'sem_guia_irpj_csll',
    campo: 'sem_guia_irpj_csll',
    rotulo: 'Sem guia IRPJ/CSLL',
    cor: '#0EA5E9',
    icone: <Hourglass size={18} weight="duotone" />,
    dica: 'IRPJ/CSLL devido e a guia ainda não saiu. O resto bate, então a empresa não conta como erro: fica aguardando a guia.',
  },
  {
    situacao: 'quotas',
    campo: 'quotas',
    rotulo: 'Quotas',
    cor: '#7C3AED',
    icone: <Coins size={18} weight="duotone" />,
  },
  {
    situacao: 'outro',
    campo: 'outros',
    rotulo: 'Auditadas (Emp. Obs)',
    cor: '#475569',
    icone: <Eye size={18} weight="duotone" />,
  },
  {
    situacao: 'total_fatura',
    campo: 'total_fatura',
    rotulo: 'Emp. c/ fatura',
    cor: '#EA580C',
    icone: <Receipt size={18} weight="duotone" />,
    dica: 'Empresas que emitiram notas do tipo fatura nos últimos 3 meses',
  },
  {
    situacao: 'receitas_financeiras',
    campo: 'receitas_financeiras',
    rotulo: 'Receita financeira',
    cor: '#B45309',
    icone: <Warning size={18} weight="duotone" />,
    dica: 'Empresas com receita financeira marcada como faturamento no Domínio (acumulador com "compõe faturamento"). Nesta tela ela é retirada do faturamento e do cálculo agente.',
    semPercentual: true,
  },
  {
    situacao: 'receitas_financeiras_fora',
    campo: 'receitas_financeiras_fora',
    rotulo: 'Rec. financeira fora do fat.',
    cor: '#0284C7',
    icone: <Info size={18} weight="duotone" />,
    dica: 'Empresas com receita financeira no mês já cadastrada fora do faturamento no Domínio (cadastro certo). Só informativo: nada é descontado.',
    semPercentual: true,
  },
];

// XML × Domínio: a mesma conferência nota a nota da tela Conferência Fiscal,
// rodando sozinha em segundo plano para a carteira LP/LR.
const KPIS_XML: IKpi[] = [
  {
    situacao: 'notas_fora_dominio',
    campo: 'notas_fora_dominio',
    rotulo: 'Notas fora do Domínio',
    cor: '#DC2626',
    icone: <FileX size={18} weight="duotone" />,
    dica: 'XML que o Domínio não reflete: nota só no XML (entra no agente), cancelada no XML e ativa no Domínio (sai do agente) ou NFS-e com valor diferente (só sinaliza). Nota que o Domínio lançou no mês vizinho não conta.',
  },
  {
    situacao: 'sem_xml',
    campo: 'sem_xml',
    rotulo: 'Sem XMLs',
    cor: '#64748B',
    icone: <FileDotted size={18} weight="duotone" />,
    dica: 'Empresa com faturamento e nenhuma nota em XML no mês: o agente usa só o Domínio.',
  },
];

const textoXmlCalculado = (iso?: string | null): string => {
  if (iso === undefined) return '';
  if (!iso) return 'calculando os XML do mês…';
  return `XML conferidos ${moment(iso).fromNow()}`;
};

const KpiSituacao = ({
  kpi,
  totais,
  carregando,
  ativo,
  onClick,
}: {
  kpi: IKpi;
  totais?: ITotais;
  carregando: boolean;
  ativo: boolean;
  onClick: () => void;
}) => {
  const valor = Number(totais?.[kpi.campo] ?? 0);
  const pct = totais?.[`${kpi.campo}_%`];
  return (
    <Tooltip
      label={kpi.dica}
      isDisabled={!kpi.dica}
      fontSize="11px"
      hasArrow
      placement="top"
      maxW="260px"
      openDelay={300}
    >
      <Box
        as="button"
        type="button"
        onClick={onClick}
        aria-pressed={ativo}
        textAlign="left"
        w="full"
        p="10px 12px"
        borderRadius="14px"
        border="1.5px solid"
        borderColor={ativo ? kpi.cor : 'rgba(15,23,42,0.07)'}
        bg={ativo ? `${kpi.cor}14` : 'rgba(255,255,255,0.65)'}
        boxShadow={ativo ? `0 0 0 3px ${kpi.cor}14` : 'none'}
        transition="all 0.14s"
        _hover={{ borderColor: `${kpi.cor}66`, transform: 'translateY(-1px)' }}
        _focusVisible={{ outline: `2px solid ${kpi.cor}`, outlineOffset: '2px' }}
      >
        <Flex align="center" gap="10px">
          <Flex
            w="32px"
            h="32px"
            borderRadius="10px"
            align="center"
            justify="center"
            flex="none"
            bg={`${kpi.cor}1A`}
            color={kpi.cor}
          >
            {kpi.icone}
          </Flex>
          <Box minW={0}>
            <Text fontSize="10.5px" color="#64748B" fontWeight={600} lineHeight="1.25">
              {kpi.rotulo}
            </Text>
            {carregando ? (
              <Spinner size="xs" color={kpi.cor} mt={1} />
            ) : (
              <Flex align="baseline" gap="6px">
                <Text
                  fontSize="18px"
                  fontWeight={800}
                  color="#0F172A"
                  lineHeight="1.15"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {valor.toLocaleString('pt-BR')}
                </Text>
                {!kpi.semPercentual && pct !== undefined && pct !== null && (
                  <Text fontSize="10.5px" color="#94A3B8" fontWeight={600}>
                    {pct}%
                  </Text>
                )}
              </Flex>
            )}
          </Box>
        </Flex>
      </Box>
    </Tooltip>
  );
};

type FormValues = {
  search: string;
  ocultar: ISelect<string>;
  grupo_de_empresas: ISelect<string>[];
  range: number[];
} & Record<string, any>;

export function SupervisorLucroPage({ regime }: { regime: Regime }) {
  const cfg = CONFIG_REGIME[regime];
  const { getCompanies, getCompaniesFilters, getQuebrasAnalisesTotais } = useCompanies();
  const { createQuota, deleteQuota } = useEmpresaQuota();
  const queryClient = useQueryClient();
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();
  const [range, setRange] = useState([0, 100]);
  const [tempRange, setTempRange] = useState([0, 100]);
  const rangeSliderId = useId();

  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    currentDate: currentFilterDate,
    dateStart,
    dateEnd,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({
    additionalParams: {
      screen_data: 'fiscal',
      regime,
      search: '',
      qtd_apuracoes: '0,100',
    },
  });

  const filtersFormMethods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
      range: [0, 100],
    },
  });

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  // Busca com modo selecionável (ID é o padrão)
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');
    if (searchValue) {
      switch (searchModeRef.current) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') debouncedSearch(values.search || '');
      if (name === 'ocultar') {
        const newFilters = new URLSearchParams(filters);
        if (values.ocultar?.value) newFilters.set('ocultar', values.ocultar.value);
        else newFilters.delete('ocultar');
        newFilters.set('page', '1');
        setFilters(newFilters);
      }
    });
    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch, filters]);

  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  const activeFiltersCount = useMemo(() => {
    let count = filters.get('id') ? 1 : 0;
    count += FILTROS_AVANCADOS.filter(f => filters.get(f.name)).length;
    const grupos = filters.get('grupo_de_empresas');
    if (grupos) count += grupos.split(',').filter(g => g.trim()).length;
    return count;
  }, [filters]);
  const hasActiveFilters = activeFiltersCount > 0;

  /** Parâmetros que sobrevivem a aplicar/limpar os filtros avançados. */
  const paramsBase = () => {
    const p = new URLSearchParams({
      page: '1',
      size: '50',
      screen_data: 'fiscal',
      regime,
      search: filters.get('search') || '',
      date_start: filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    });
    ['external_id', 'cnpj', 'situacao', 'ocultar'].forEach(k => {
      const v = filters.get(k);
      if (v) p.set(k, v);
    });
    return p;
  };

  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = paramsBase();
    newFilters.set('qtd_apuracoes', range.join(','));
    if (values.ocultar?.value) newFilters.set('ocultar', values.ocultar.value);
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    FILTROS_AVANCADOS.forEach(f => {
      if (values[f.name]?.value) newFilters.set(f.name, values[f.name].value);
    });
    if (values.grupo_de_empresas?.length) {
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.map(g => g.value).join(','));
    }
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    const cleared = paramsBase();
    cleared.set('qtd_apuracoes', '0,100');
    setFilters(cleared);
    setRange([0, 100]);
    setTempRange([0, 100]);
    filtersFormMethods.setValue('companies', undefined);
    FILTROS_AVANCADOS.forEach(f => filtersFormMethods.setValue(f.name, undefined));
    filtersFormMethods.setValue('grupo_de_empresas', []);
    filtersFormMethods.setValue('range', [0, 100]);
  };

  const situacaoAtual = filters.get('situacao');
  const alternarSituacao = (situacao: string) => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    if (situacaoAtual === situacao) newFilters.delete('situacao');
    else newFilters.set('situacao', situacao);
    newFilters.set('page', '1');
    setFilters(newFilters);
  };

  // Totais sem 'situacao' na chave: filtrar por um card não recarrega os cards
  const totaisFilters = useMemo(
    () => ({
      ...Object.fromEntries([...Array.from(filters)].filter(([key]) => key !== 'situacao')),
      ...PARAMS_TELA,
    }),
    [filters],
  );

  const { data: totais, isFetching: isFetchingTotais } = useQuery<ITotais>({
    queryKey: [`getQuebrasTotais-${cfg.chave}`, totaisFilters],
    queryFn: getQuebrasAnalisesTotais,
  });

  const { data: CompaniesFilters } = useQuery({
    queryKey: [`CompaniesFilters-${cfg.chave}`],
    queryFn: getCompaniesFilters,
  });

  useQuery<IData<ICompany[]>>({
    queryKey: [`CompaniesSelect-${cfg.chave}`, { page: 1, size: 30, search: '' }],
    queryFn: getCompanies,
  });

  const infiniteQueryFilters = useMemo(() => {
    const base = cleanFilters(Object.fromEntries([...Array.from(filters)]));
    delete base.page;
    delete base.size;
    return { ...base, ...PARAMS_TELA };
  }, [filters]);

  const {
    data: infiniteData,
    isLoading: isLoadingCompanies,
    isFetching: isFetchingCompany,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<IData<ICompany[]>>({
    queryKey: [`CompaniesList-${cfg.chave}`, infiniteQueryFilters],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
        params: { ...infiniteQueryFilters, page: pageParam, size: ITEMS_PER_PAGE },
      });
      return res.data;
    },
    getNextPageParam: lastPage =>
      lastPage?.data?.page < lastPage?.data?.pages ? lastPage.data.page + 1 : undefined,
    initialPageParam: 1,
  });

  const companies = useMemo<ICompany[]>(() => {
    if (!infiniteData?.pages) return [];
    const vistos = new Set<number>();
    const todas: ICompany[] = [];
    infiniteData.pages.forEach(page => {
      page?.data?.items?.forEach(company => {
        if (!vistos.has(company.id)) {
          vistos.add(company.id);
          todas.push(company);
        }
      });
    });
    return todas;
  }, [infiniteData?.pages]);
  const totalLista = infiniteData?.pages?.[0]?.data?.total ?? 0;

  const hasMore = hasNextPage ?? false;
  const loadMore = useCallback(() => {
    if (hasMore && !isFetchingNextPage) fetchNextPage();
  }, [hasMore, isFetchingNextPage, fetchNextPage]);

  // Infinite scroll
  useEffect(() => {
    if (companies.length === 0 || (!hasMore && !isFetchingNextPage)) return undefined;
    let observer: IntersectionObserver | null = null;
    let attempts = 0;
    const trySetupObserver = () => {
      attempts += 1;
      const currentRef = loadMoreRef.current;
      if (!currentRef) {
        if (attempts < 10) setTimeout(trySetupObserver, 50);
        return;
      }
      observer = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting && hasMore && !isFetchingNextPage) loadMore();
        },
        { threshold: 0.1 },
      );
      observer.observe(currentRef);
    };
    const timeoutId = setTimeout(trySetupObserver, 100);
    return () => {
      clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [loadMore, companies.length, hasMore, isFetchingNextPage]);

  const quotaMutation = useMutation({
    mutationFn: async ({ empresaId, isQuota }: { empresaId: number; isQuota: boolean }) => {
      const dtCompetencia =
        filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD');
      const payload = { empresa_id: empresaId, dt_competencia: dtCompetencia, tela: 'fiscal' };
      return isQuota ? deleteQuota(payload) : createQuota(payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [`CompaniesList-${cfg.chave}`] });
      queryClient.invalidateQueries({ queryKey: [`getQuebrasTotais-${cfg.chave}`] });
      toast.success(
        variables.isQuota ? 'Empresa removida das quotas' : 'Empresa marcada como quota',
        { position: 'top-right' },
      );
    },
    onError: () => {
      toast.error('Erro ao atualizar quota', { position: 'top-right' });
    },
  });

  const kpis = (lista: IKpi[]) =>
    lista.map(kpi => (
      <KpiSituacao
        key={kpi.situacao}
        kpi={kpi}
        totais={totais}
        carregando={isFetchingTotais}
        ativo={situacaoAtual === kpi.situacao}
        onClick={() => alternarSituacao(kpi.situacao)}
      />
    ));

  return (
    <GlassPageBg>
      {/* Rota glass (isGlass): sem a moldura do layout, o espaçamento é da página */}
      <Flex direction="column" gap={5} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* Cabeçalho */}
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/supervisor" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(99,102,241,0.45)"
          >
            <Scales size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Supervisor › {cfg.titulo}
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em" color="#0F172A">
              {cfg.titulo}
            </Text>
          </Box>
          <Box flex={1} />
          {(isFetchingTotais || isFetchingCompany) && (
            <Flex role="status" aria-live="polite" align="center" gap={2}>
              <Spinner size="sm" color="#1B88F5" />
              <Text fontSize="13px" color="#64748B">
                Carregando…
              </Text>
            </Flex>
          )}
          <MonthNavigator
            currentDate={currentFilterDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onSelectMonth={handleSelectMonth}
            compact
          />
        </Flex>

        {/* Situação do mês — cada número filtra a lista */}
        <GlassCard intensity="strong" p="14px 16px">
          <Text
            fontSize="10.5px"
            fontWeight={700}
            color="#94A3B8"
            letterSpacing="0.12em"
            textTransform="uppercase"
            mb={2}
          >
            Apuração
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={2}>
            {kpis(KPIS_APURACAO)}
          </SimpleGrid>
          <Text
            fontSize="10.5px"
            fontWeight={700}
            color="#94A3B8"
            letterSpacing="0.12em"
            textTransform="uppercase"
            mt={4}
            mb={2}
          >
            Conferência
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4, '2xl': 9 }} spacing={2}>
            {kpis(KPIS_CONFERENCIA)}
          </SimpleGrid>
          <Flex align="baseline" gap={2} mt={4} mb={2}>
            <Text
              fontSize="10.5px"
              fontWeight={700}
              color="#94A3B8"
              letterSpacing="0.12em"
              textTransform="uppercase"
            >
              XML × Domínio
            </Text>
            <Text fontSize="10.5px" color="#94A3B8">
              {textoXmlCalculado(totais?.xml_calculado_em)}
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 2, md: 4, '2xl': 9 }} spacing={2}>
            {kpis(KPIS_XML)}
          </SimpleGrid>
        </GlassCard>

        {/* Busca e filtros */}
        <GlassCard intensity="strong" p="12px 14px" overflow="visible">
          <FormProvider {...filtersFormMethods}>
            <Flex
              align={{ base: 'stretch', md: 'center' }}
              justify="space-between"
              gap={3}
              direction={{ base: 'column', md: 'row' }}
            >
              <Box w={{ base: 'full', md: '380px' }}>
                <SearchModeBar
                  modes={MODES_ID_NOME_CNPJ}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as 'ID' | 'Nome' | 'CNPJ')}
                  value={filtersFormMethods.watch('search') ?? ''}
                  onValueChange={v =>
                    filtersFormMethods.setValue('search', v, { shouldDirty: true })
                  }
                />
              </Box>
              <Flex
                align="center"
                gap={2}
                wrap="wrap"
                justify={{ base: 'flex-start', md: 'flex-end' }}
              >
                <Text fontSize="12px" color="#64748B" fontWeight={600} mr={1}>
                  {totalLista.toLocaleString('pt-BR')} {totalLista === 1 ? 'empresa' : 'empresas'}
                </Text>
                <Box minW="150px">
                  <SelectInputForm<{ label: string; value: string }>
                    name="ocultar"
                    placeholder="Ocultar"
                    options={OCULTAR_OPTIONS}
                    controlMb={0}
                    isClearable
                    chakraStyles={{
                      control: styles => ({
                        ...styles,
                        minH: '32px',
                        height: '32px',
                        borderRadius: '10px',
                      }),
                    }}
                  />
                </Box>
                <Popover>
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="outline"
                      borderRadius="10px"
                      leftIcon={<Coins size={15} />}
                    >
                      Apurações ({range[0]} - {range[1]})
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent w="320px">
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader fontWeight="semibold">
                      Filtrar por quantidade de apurações
                    </PopoverHeader>
                    <PopoverBody pt={6}>
                      <Box px={2}>
                        <label htmlFor={rangeSliderId} className="mb-2 block text-sm font-medium">
                          Intervalo: {tempRange[0]} - {tempRange[1]}
                        </label>
                        <RangeSlider
                          id={rangeSliderId}
                          defaultValue={[0, 100]}
                          value={tempRange}
                          onChange={setTempRange}
                          min={0}
                          max={100}
                          step={1}
                          colorScheme="blue"
                        >
                          <RangeSliderTrack>
                            <RangeSliderFilledTrack />
                          </RangeSliderTrack>
                          <Tooltip hasArrow placement="top" label={`${tempRange[0]}`}>
                            <RangeSliderThumb index={0} />
                          </Tooltip>
                          <Tooltip hasArrow placement="top" label={`${tempRange[1]}`}>
                            <RangeSliderThumb index={1} />
                          </Tooltip>
                        </RangeSlider>
                        <Button
                          colorScheme="blue"
                          size="sm"
                          w="full"
                          mt={4}
                          onClick={() => {
                            setRange(tempRange);
                            filtersFormMethods.setValue('range', tempRange);
                            const newFilters = new URLSearchParams(filters);
                            newFilters.set('qtd_apuracoes', tempRange.join(','));
                            newFilters.set('page', '1');
                            setFilters(newFilters);
                          }}
                        >
                          Aplicar
                        </Button>
                      </Box>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="10px"
                  leftIcon={<FunnelSimple size={16} />}
                  colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                  onClick={onFilterDrawerOpen}
                >
                  Filtros{hasActiveFilters ? ` (${activeFiltersCount})` : ''}
                </Button>
              </Flex>
            </Flex>

            <FilterDrawer
              isOpen={isFilterDrawerOpen}
              onClose={onFilterDrawerClose}
              title="Filtros Avançados"
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
              onApplyFilters={handleApplyFilters}
              formMethods={filtersFormMethods}
              size="lg"
            >
              <form className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {FILTROS_AVANCADOS.slice(0, 2).map(f => (
                  <SelectInputForm<ISelect<string>>
                    key={f.name}
                    name={f.name}
                    placeholder={f.placeholder}
                    options={getFormattedLabelString(CompaniesFilters?.[f.name])}
                    controlMb={0}
                  />
                ))}
                <MultiSelectInputForm<ISelect<string>>
                  name="grupo_de_empresas"
                  placeholder="Selecione até 4 grupos"
                  options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                  maxSelectedOptions={4}
                  controlMb={0}
                />
                {FILTROS_AVANCADOS.slice(2).map(f => (
                  <SelectInputForm<ISelect<string>>
                    key={f.name}
                    name={f.name}
                    placeholder={f.placeholder}
                    options={getFormattedLabelString(CompaniesFilters?.[f.name])}
                    controlMb={0}
                  />
                ))}
              </form>
            </FilterDrawer>
          </FormProvider>
        </GlassCard>

        {/* Empresas */}
        {isLoadingCompanies && (
          <Flex direction="column" align="center" gap={3} py={12}>
            <Spinner size="lg" color="#1B88F5" thickness="3px" />
            <Text fontSize="13px" color="#64748B">
              Carregando empresas…
            </Text>
          </Flex>
        )}
        {!isLoadingCompanies && companies.length === 0 && !isFetchingCompany && (
          <GlassCard intensity="medium" p={10}>
            <Flex direction="column" align="center" gap={2}>
              <Buildings size={40} color="#CBD5E1" />
              <Text fontSize="13px" color="#64748B">
                Nenhuma empresa encontrada.
              </Text>
            </Flex>
          </GlassCard>
        )}
        {companies.length > 0 && (
          <Flex direction="column" gap={4}>
            {companies.map(item => (
              <EmpresaCard
                key={item.id}
                item={item}
                cfg={cfg}
                dateStart={dateStart}
                dateEnd={dateEnd}
                quotaPendente={
                  quotaMutation.isPending && quotaMutation.variables?.empresaId === item.id
                }
                onQuota={() =>
                  quotaMutation.mutate({
                    empresaId: item.id,
                    isQuota: item.paga_por_quotas ?? false,
                  })
                }
              />
            ))}
          </Flex>
        )}

        {/* Sentinel do infinite scroll */}
        <div ref={loadMoreRef} className="h-10 w-full" />
        {isFetchingNextPage && (
          <Flex align="center" justify="center" py={4} gap={3}>
            <Spinner size="md" color="#1B88F5" />
            <Text color="#64748B" fontSize="13px">
              Carregando mais…
            </Text>
          </Flex>
        )}
        {!hasMore && companies.length > 0 && (
          <Text textAlign="center" color="#94A3B8" fontSize="12px" py={2}>
            Fim da lista
          </Text>
        )}
      </Flex>
    </GlassPageBg>
  );
}
